import { useState, useEffect, useMemo, useCallback } from "react";
import { Grid, Typography, debounce, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { FormProvider, useForm } from "react-hook-form";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormTextField from "./../../../../Controllers/Input/index";
import service from "./../../helpers/GifService";
import { useDispatch, useSelector } from "react-redux";
import { setData, setServiceFunction } from "./../../../../store/formSlice";
import {
  debounceDuration,
  safeValue,
} from "./../../siteMap/navigations/helpers";
import ApplicantSelector from "../../Components/applicantSelector";
import {
  canEditSelector,
  reportingAsSelector,
} from "../../../../store/applicantsSlice";
import FormAutoComplete from "../../../../Controllers/AutoComplete";
import { useLoading } from "../../../../providers/loadingProvider";
import { useLocation } from "react-router";
import { proposalSelector } from "../../../../store/proposalSlice";
import { PRE_PROPOSAL_STATUS } from "../../Constants";

const pageName = "Basics";

const useStyles = makeStyles((theme) => ({
  infoIcon: {
    marginLeft: 5,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
}));
const TITLE_FIELD_NAME = "title";
const FIRST_NAME_FIELD_NAME = "first_name";
const LAST_NAME_FIELD_NAME = "last-name";
const INSTITUTION_FIELD_NAME = "institution";
const FACULTY_FIELD_NAME = "faculty_name";
const DEPARTMENT_NAME_FIELD_NAME = "department_name";
const INSTITUTION_COUNTRY_FIELD_NAME = "institution_country";
const PRIMARY_EMAIL_FIELD_NAME = "primary_email";
const SECONDARY_EMAIL_FIELD_NAME = "secondary_email";
const OFFICE_PHONE_FIELD_NAME = "office_phone";
const CELL_PHONE_FIELD_NAME = "cell_phone";

export default function Basics() {
  const methods = useForm();
  const classes = useStyles();
  const [titleOptions, setTitleOptions] = useState([]);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const dispatch = useDispatch();
  const proposal = useSelector(proposalSelector);
  const { setValue, getValues, formState, watch } = methods;
  const watchAll = watch();
  const loading = useLoading();
  const as = useSelector(reportingAsSelector);
  const canEdit = useSelector(canEditSelector);
  const editPreProposal = useMemo(
    () => proposal?.status === PRE_PROPOSAL_STATUS,
    [proposal]
  );
  const focus = new URLSearchParams(useLocation().search).get("focus");

  const getInstitutions = useCallback(async () => {
    const institutions = (await service.getAllInstitutions()).map((i) => ({
      id: i.institutionId,
      label: i.name,
    }));
    setInstitutionOptions(institutions);
  }, []);

  useEffect(() => {
    dispatch(setServiceFunction(`updateApplicant`));
  }, [dispatch]);

  const debouncedSetData = useMemo(
    () =>
      debounce(() => {
        const dirty = Object.keys(formState.dirtyFields);
        const values = getValues(dirty);
        const applicantData = {
          firstName: values[FIRST_NAME_FIELD_NAME],
          lastName: values[LAST_NAME_FIELD_NAME],
          institution: values[INSTITUTION_FIELD_NAME],
          titleCode: values[TITLE_FIELD_NAME],
          faculty: values[FACULTY_FIELD_NAME],
          department: values[DEPARTMENT_NAME_FIELD_NAME],
          primaryEmail: values[PRIMARY_EMAIL_FIELD_NAME],
          secondaryEmail: values[SECONDARY_EMAIL_FIELD_NAME],
          workPhone: values[OFFICE_PHONE_FIELD_NAME],
          phoneCell: values[CELL_PHONE_FIELD_NAME],
        };

        dispatch(
          setData({
            applicantData: applicantData,
          })
        );
      }, debounceDuration),
    [dispatch, getValues, formState]
  );

  useEffect(() => {
    debouncedSetData();
  }, [debouncedSetData, watchAll]);

  const fillTitlesOptions = useCallback(async () => {
    const loadingId = loading.addLoading("loading titles");
    var titles = await service.GetContactTitles();
    const titlesList = titles.map((title) => ({
      id: title.value,
      label: title.label,
    }));
    setTitleOptions(titlesList);
    loading.removeLoading(loadingId);
  }, [loading]);

  useEffect(() => {
    const fillApplicantData = async () => {
      const loadingId = loading.addLoading("loading applicant");
      await fillTitlesOptions();
      await getInstitutions();
      const applicantData = await service.getContactRishum();
      setValue(FIRST_NAME_FIELD_NAME, safeValue(applicantData.firstName));
      setValue(LAST_NAME_FIELD_NAME, safeValue(applicantData.lastName));
      setValue(INSTITUTION_FIELD_NAME, safeValue(applicantData.institution));
      setValue(
        INSTITUTION_COUNTRY_FIELD_NAME,
        safeValue(applicantData.country)
      );
      setValue(PRIMARY_EMAIL_FIELD_NAME, safeValue(applicantData.primaryEmail));
      setValue(FACULTY_FIELD_NAME, safeValue(applicantData.faculty));
      setValue(DEPARTMENT_NAME_FIELD_NAME, safeValue(applicantData.department));
      setValue(
        SECONDARY_EMAIL_FIELD_NAME,
        safeValue(applicantData.secondaryEmail)
      );
      setValue(OFFICE_PHONE_FIELD_NAME, safeValue(applicantData.workPhone));
      setValue(CELL_PHONE_FIELD_NAME, safeValue(applicantData.phoneCell));
      setValue(TITLE_FIELD_NAME, safeValue(applicantData.titleCode));
      loading.removeLoading(loadingId);
    };
    fillApplicantData();
  }, [setValue, as, loading, fillTitlesOptions, getInstitutions]);

  return (
    <FormProvider {...methods}>
      <Box className={classes.header}>
        <Typography variant="subtitle2">{pageName}</Typography>
        <InfoOutlinedIcon color="primary" />
        <ApplicantSelector />
      </Box>
      <form>
        <Grid container spacing={4}>
          <Grid item xs={12} />
          <Grid item xs={2}>
            <FormAutoComplete
              autoFocus={focus === TITLE_FIELD_NAME}
              name={TITLE_FIELD_NAME}
              label="Title"
              options={titleOptions}
              variant={canEdit || !editPreProposal ? "outlined" : "filled"}
              disabled={!canEdit || !editPreProposal}
            />
          </Grid>
          <Grid item xs={5}>
            <FormTextField
              autoFocus={focus === FIRST_NAME_FIELD_NAME}
              name={FIRST_NAME_FIELD_NAME}
              label="First Name"
              variant={canEdit || !editPreProposal ? "outlined" : "filled"}
              disabled={!canEdit || !editPreProposal}
            />
          </Grid>
          <Grid item xs={5}>
            <FormTextField
              autoFocus={focus === LAST_NAME_FIELD_NAME}
              name={LAST_NAME_FIELD_NAME}
              label="Last Name"
              variant={canEdit || !editPreProposal ? "outlined" : "filled"}
              disabled={!canEdit || !editPreProposal}
            />
          </Grid>
          <Grid item xs={6}>
            <FormAutoComplete
              autoFocus={focus === INSTITUTION_FIELD_NAME}
              name={INSTITUTION_FIELD_NAME}
              label="Name Of Institution"
              options={institutionOptions}
              variant={canEdit || !editPreProposal ? "outlined" : "filled"}
              disabled={!canEdit || !editPreProposal}
            />
          </Grid>
          <Grid item xs={6}>
            <FormTextField
              autoFocus={focus === FACULTY_FIELD_NAME}
              name={FACULTY_FIELD_NAME}
              label="Faculty"
              variant={canEdit ? "outlined" : "filled"}
              disabled={!canEdit || !editPreProposal}
            />
          </Grid>
          <Grid item xs={6}>
            <FormTextField
              autoFocus={focus === DEPARTMENT_NAME_FIELD_NAME}
              name={DEPARTMENT_NAME_FIELD_NAME}
              label="Department"
              variant={canEdit ? "outlined" : "filled"}
              disabled={!canEdit || !editPreProposal}
            />
          </Grid>
          <Grid item xs={6}>
            <FormTextField
              autoFocus={focus === INSTITUTION_COUNTRY_FIELD_NAME}
              name={INSTITUTION_COUNTRY_FIELD_NAME}
              label="Country of institution"
              variant="filled"
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <FormTextField
              autoFocus={focus === PRIMARY_EMAIL_FIELD_NAME}
              name={PRIMARY_EMAIL_FIELD_NAME}
              label="Primary Email"
              variant="filled"
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <FormTextField
              autoFocus={focus === SECONDARY_EMAIL_FIELD_NAME}
              name={SECONDARY_EMAIL_FIELD_NAME}
              label="Secondary Email"
              variant={canEdit ? "outlined" : "filled"}
              disabled={!canEdit || !editPreProposal}
            />
          </Grid>
          <Grid item xs={6}>
            <FormTextField
              autoFocus={focus === OFFICE_PHONE_FIELD_NAME}
              name={OFFICE_PHONE_FIELD_NAME}
              label="Office phone"
              variant={canEdit ? "outlined" : "filled"}
              disabled={!canEdit || !editPreProposal}
            />
          </Grid>
          <Grid item xs={6}>
            <FormTextField
              autoFocus={focus === CELL_PHONE_FIELD_NAME}
              name={CELL_PHONE_FIELD_NAME}
              label="Cell phone"
              variant={canEdit ? "outlined" : "filled"}
              disabled={!canEdit || !editPreProposal}
            />
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}
